import React from 'react';
import { useHistory } from 'react-router-dom';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  Chip,
  Divider,
  Link,
  MenuItem,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

import { EeAutocomplete } from 'components/EeAutocomplete/EeAutocomplete';
import { Menu } from 'components/Menu';
import { Section } from 'components/Section';
import { Company, Tag, TagsTagTypeChoices } from 'libs/api/invest/generated/schema';
import { useQuery } from 'utils/graphql';

import { CapTableReport } from './components/CapTableReport';
import './styles.scss';

interface IMenuItem {
  divider?: boolean;
  href?: string;
  title?: string;
  label?: string;
  disabled?: boolean;
  isGroup?: boolean;
  onClick?: () => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    menuGroup: {
      fontWeight: 600,
      color: '#1e1e1e',
      opacity: '1 !important',
    },
    link: {
      color: '#16181b',
      fontSize: '13px',
      width: '100%',
      '&:hover': {
        color: '#16181b',
        textDecoration: 'none',
      },
    },
    chip: {
      backgroundColor: '#28a745',
      borderRadius: '.25rem',
      color: '#fff',
      fontSize: '75%',
      fontWeight: 700,
      height: '18px',
      marginLeft: '5px',
      transition:
        'color .15s ease-in-out,background-color .15s ease-in-out,border-color .15s ease-in-out,box-shadow .15s ease-in-out',
    },
  }),
);

export function CompanySelector(
  props: {
    companies: Company[]
    company: Company
    companyIsLoading: boolean
    reloadCompany: (
      companyId: string,
      aggregationType: TagsTagTypeChoices|undefined,
      aggregationId: string|null,
      currencyCode?: string
    ) => Promise<void>
  }
) {
  const history = useHistory();
  const aggregations = [{name: 'All Funds', id: null}].concat(
    getUniqueItems(
      props.company.allFundInvestments
      .map(fi => fi.fund.divisions)
      .flat()
      .map(d => ({...d, verboseType: 'Divisions'}))
      .sort((a, b) => a.name.localeCompare(b.name))
    ),
    getUniqueItems(
      props.company.allFundInvestments
      .map(fi => fi.fund.groups)
      .flat()
      .map(g => ({...g, verboseType: 'Fund Groups'}))
      .sort((a, b) => a.name.localeCompare(b.name))
    ),
    props.company.allFundInvestments
      .map(fi => ({
        name: fi.fund.name,
        id: fi.fund.id,
        verboseType: 'Funds',
        fundInvestmentId: fi.id,
      }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  );
  const currencies = (props.company.currency ? [props.company.currency] : [])
    .concat(props.company.allFundInvestments.map(fi => fi.fund.currency))
    .filter((v, i, a) => a.indexOf(v) === i);  // unique values

  // Also add the default currency of the company
  if (props.company.rawCurrency && !currencies.includes(props.company.rawCurrency)) {
    currencies.push(props.company.rawCurrency)
  }

  const classes = useStyles();
  const [selectedAggregation, setSelectedAggregation] = React.useState<
    {
      name: string,
      id: string|null,
      type?: TagsTagTypeChoices,
      fundInvestmentId?: string,
    }
  >(
    aggregations.find(a => a.id === props.company.aggregationId) || aggregations[0]
  );
  const [selectedCompany, setSelectedCompany] = React.useState(props.company);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [capTableReportFormOpen, setCapTableReportFormOpen] = React.useState(false);

  const user = useQuery(
    {
      currentUser: {
        __scalar: 1,
        ventureCapital: {
          name: 1,
          siteMode: 1,
        },
      }
    },
    'currentUser',
  );

  React.useEffect(() => {
    setSelectedAggregation(aggregations.find(a => a.id === props.company.aggregationId) || aggregations[0]);
    setSelectedCompany(props.company);

    const queryParams = new URLSearchParams(window.location.search);
    props.company.currency
      ? queryParams.set('currency', props.company.currency)
      : queryParams.delete('currency');
    props.company.aggregationType
      ? queryParams.set('aggrType', props.company.aggregationType)
      : queryParams.delete('aggrType');
    props.company.aggregationId
      ? queryParams.set('aggrId', props.company.aggregationId)
      : queryParams.delete('aggrId');

    history.replace(`/company/${props.company.id}?${queryParams.toString()}`);
  }, [props.company]);

  const menuItems: IMenuItem[] = [
    {
      href: `/company/${props.company.id}/cap-table/`,
      title: 'View Cap Table',
    },
    {
      href: `/company/${props.company.id}/pdf?${new URLSearchParams(window.location.search.toString())}&asofdate=${props.company.asOfDate.format('YYYYMMDD')}`,
      title: 'Export as PDF',
    },
    {
      title: 'Export Detailed Cap Table Report',
      onClick: () => { setCapTableReportFormOpen(true); },
      disabled: !selectedAggregation.fundInvestmentId,
    },
    {
      href: `/investment/${selectedAggregation.fundInvestmentId || ''}/status`,
      title: 'Status',
      disabled: !selectedAggregation.fundInvestmentId,
    },
  ];
  if (user.data?.ventureCapital?.siteMode === 'A_10') {
    menuItems.push(...[
      {
        divider: true,
      },
      {
        title: 'Admin',
        isGroup: true,
      },
      {
        href: `/admin/fund-investment/${selectedAggregation.fundInvestmentId || ''}/financing-rounds/`,
        title: 'Financing Rounds',
        disabled: !selectedAggregation.fundInvestmentId,
      },
      {
        href: `/admin/fund-investment/${selectedAggregation.fundInvestmentId || ''}/wires/`,
        title: 'Investments',
        disabled: !selectedAggregation.fundInvestmentId,
      },
      {
        href: `/admin/fund-investment/${selectedAggregation.fundInvestmentId || ''}/realized/`,
        title: 'Realized Values',
        disabled: !selectedAggregation.fundInvestmentId,
      },
      {
        href: `/admin/fund-investment/${selectedAggregation.fundInvestmentId || ''}/valuations/`,
        title: 'Manual Valuations',
        disabled: !selectedAggregation.fundInvestmentId,
      },
      {
        href: `/admin/fund-investment/${selectedAggregation.fundInvestmentId || ''}/calculated-values/`,
        title: 'Calculated Values',
        disabled: !selectedAggregation.fundInvestmentId,
      },
      {
        href: `/admin/company/${props.company.id}/details/`,
        title: 'Company Details',
      },
      {
        href: `/admin/company/${props.company.id}/metrics/`,
        title: 'Key Metrics',
      },
      {
        href: `/admin/company/${props.company.id}/financials/`,
        title: 'Financials',
      },
      {
        href: `/admin/company/${props.company.id}/headcounts/`,
        title: 'Headcount',
      },
      {
        href: `/admin/company/${props.company.id}/users/`,
        title: 'Users',
      },
    ]);
  } else {
    menuItems.push(...[
      {
        href: `/admin/fund-investment/${selectedAggregation.fundInvestmentId || ''}/valuations/`,
        title: 'Manual Valuations',
        disabled: !selectedAggregation.fundInvestmentId,
      },
      {
        href: `/admin/fund-investment/${selectedAggregation.fundInvestmentId || ''}/calculated-values/`,
        title: 'Calculated Values',
        disabled: !selectedAggregation.fundInvestmentId,
      },
      {
        href: `/admin/company/${props.company.id}/details/`,
        title: 'Company Details',
      },
    ]);
  }
  menuItems.push(...[
    {
      href: `/admin/company/${props.company.id}/sentiment/`,
      title: 'Quarterly Sentiment',
    },
  ]);

  function getUniqueItems(items: Tag[]) {
    return Array.from(new Map(items.map(i => [i.id, i])).values());
  }

  function handleMenuClose() {
    setMenuAnchorEl(null);
  }

  return (
    <Section>
      <div className="d-flex align-items-center w-100 px-3 py-1 justify-content-between">
        {!selectedAggregation.type && selectedAggregation.fundInvestmentId &&
          <CapTableReport
            fundInvestmentId={selectedAggregation.fundInvestmentId}
            isOpen={capTableReportFormOpen}
            onClose={() => setCapTableReportFormOpen(false)}
          />
        }
        <div className="d-flex align-items-start">
          <EeAutocomplete
            disabled={props.companyIsLoading}
            value={(props.companies || []).find(c => c.id === selectedCompany.id)}
            onChange={(e, option: Company) => {
              setSelectedCompany(option);
              setSelectedAggregation(aggregations[0]);
              props.reloadCompany(option.id, undefined, null);
            }}
            options={(props.companies || []).filter(c => !!c.allFundInvestments.length || c.id === props.company.id)}
            getOptionLabel={(option: Company) => option.name || ''}
            getOptionSelected={(option: Company) => option.id === props.company.id}
          />
          {aggregations.length &&
            <>
              <EeAutocomplete
                disabled={props.companyIsLoading}
                value={aggregations.find(a => a.id === selectedAggregation?.id)}
                onChange={(e, option: Tag) => {
                  setSelectedAggregation({...option, type: option.type || undefined});
                  props.reloadCompany(
                    props.company.id,
                    option.type || undefined,
                    option.id,
                    props.company.currency,
                  );
                }}
                options={aggregations}
                groupBy={(option: Tag&{verboseType: string}) => option.verboseType}
                getOptionLabel={(option: Tag) => option.name || ''}
                getOptionSelected={(option: Tag) => option.id === props.company.aggregationId}
              />
              <EeAutocomplete
                disabled={props.companyIsLoading}
                value={props.company.currency}
                onChange={(e: any, option: string) => {
                  props.reloadCompany(
                    props.company.id,
                    props.company.aggregationType || undefined,
                    props.company.aggregationId,
                    option,
                  );
                }}
                options={currencies}
                width={110}
              />
            </>
          }
        </div>
        <div className="d-flex align-items-end">
          <Button
            aria-controls="customized-menu"
            aria-haspopup="true"
            variant="contained"
            color="primary"
            endIcon={<ArrowDropDownIcon />}
            onClick={e => setMenuAnchorEl(e.currentTarget)}
          >
            Actions
          </Button>
          <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
            {menuItems.map((menuItem, index) =>
              menuItem.divider ? (
                <Divider key={index} />
              ) : menuItem.isGroup ? (
                <MenuItem key={index} className={classes.menuGroup} disabled>
                  {menuItem.title}
                </MenuItem>
              ) : menuItem.disabled ? (
                <MenuItem key={index} disabled>
                  <Box className={classes.link}>
                    {menuItem.title}
                    {menuItem.label &&
                      <Chip className={classes.chip} label={menuItem.label} size="small" />
                    }
                  </Box>
                </MenuItem>
              ) : menuItem.onClick ? (
                <MenuItem key={index}>
                  <Link
                    className={classes.link}
                    onClick={() => {
                      if (menuItem.onClick) {
                        menuItem.onClick();
                      }
                      handleMenuClose();
                    }}
                  >
                    {menuItem.title}
                    {menuItem.label &&
                      <Chip className={classes.chip} label={menuItem.label} size="small" />
                    }
                  </Link>
                </MenuItem>
              ) : (
                <MenuItem key={index}>
                  <Link
                    className={classes.link}
                    href={menuItem.href}
                  >
                    {menuItem.title}
                    {menuItem.label &&
                      <Chip className={classes.chip} label={menuItem.label} size="small" />
                    }
                  </Link>
                </MenuItem>
              )
            )}
          </Menu>
        </div>
      </div>
    </Section>
  );
}
